import React, { useEffect, useState } from 'react';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { useUser } from '../contexts/UserContext';
import { app } from '../firebaseConfig';
import { Loader2, ShieldAlert } from 'lucide-react';
import { Navigate } from 'react-router-dom';

const db = getFirestore(app, 'nxtquotepro');



interface Props {
  children: React.ReactNode;
}

export const AdminRoute: React.FC<Props> = ({ children }) => {
  const { user, loadingAuth } = useUser();
  
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [loadingAdmin, setLoadingAdmin] = useState(true);

  useEffect(() => {
    const checkAdmin = async () => {
      if (!user) {
        setIsAdmin(false);
        setLoadingAdmin(false);
        return;
      }

      try {
        const adminRef = doc(db, 'admin_users', user.uid);
        const adminSnap = await getDoc(adminRef);
        
        setIsAdmin(adminSnap.exists());
      } catch (err) {
        console.error('Error checking admin status:', err);
        setIsAdmin(false);
      } finally {
        setLoadingAdmin(false);
      }
    };

    if (!loadingAuth) {
      checkAdmin();
    }
  }, [user, loadingAuth]);

  if (loadingAuth || loadingAdmin) {
    return (
      <div className="min-h-screen bg-[#0A0A0F] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 size={32} className="text-purple-500 animate-spin" />
          <p className="text-gray-500 text-sm">Verifying administrative access...</p>
        </div>
      </div>
    );
  }

  if (!loadingAuth && !user) {
    return <Navigate to="/login" state={{ returnTo: '/admin/bill-review' }} />;
  }

  if (user && isAdmin === false) {
    return (
      <div className="min-h-screen bg-[#0A0A0F] flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-[#0D0D1A] border border-red-500/20 rounded-3xl p-8 text-center shadow-2xl shadow-red-500/5">
          <div className="w-16 h-16 bg-red-500/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <ShieldAlert size={32} className="text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Access Denied</h2>
          <p className="text-gray-400 text-sm leading-relaxed mb-8">
            This area is restricted to administrative personnel only. Your account does not have the required permissions to view this page.
          </p>
          <button
            onClick={() => window.location.href = '/'}
            className="w-full py-3 px-6 rounded-xl bg-white/5 border border-white/10 text-white font-semibold hover:bg-white/10 transition-all"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
